import {
  GlobalDesignPanelProps,
  GlobalDesignStyleGroup,
  StylesGroup,
  ManifestProcessingStrategy,
  LeadingStyleGroup,
} from '../../types';
import { CompRef } from '@wix/document-services-types';
import type { CssCustomProperties } from '@wix/component-protocol/schema';
import { StylesDerivedNativeActionNames } from '@wix/editor-platform-host-integration-apis';
import { VirtualManifestAPI } from '@wix/editor-platform-manifest-services';
import { GLOBAL_DESIGN_GROUPS_ACTIONS } from '../../actions';

import { applyGrouping } from './stylesGrouping';
import { buildLeadingGroups } from './comparison';
import { getStylesInfoFromGroup } from './helpers/getStylesInfoFromGroup';
import { filterStylesByActionName } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/stylesFilter';
import GlobalDesignPanelLogger from '../logging';
import { createStylesObject } from '../../helpers';
import { getStylesMetadata } from './helpers/getStylesMetadata';
import { getIsBrandColorSupported } from './helpers/getIsBrandColorSuppoerted';
import { getTargetComponents } from './helpers/getTargetComponents';
import { updateSubsetGroupsStyles } from './updateSubsetGroupsStyles';
import {
  STYLE_NAMESPACE,
  type StyleNamespace,
} from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/types';
import { getSetters, processStyleChanges } from './setters';
import { ThemesProvider } from '../abstractStyles/ThemesProvider';
import { GD_WDS_EXPERIMENT } from '../../experiments';
import type { EditorPointer } from '@wix/editor-document-logic-api';

const processElement = (
  actionName: StylesDerivedNativeActionNames,
  editorPointer: EditorPointer<CompRef>,
  context: VirtualManifestAPI,
  manifestProcessingStrategy: ManifestProcessingStrategy,
  groups: Omit<StylesGroup, 'engine'>[],
) => {
  const styles = context.resolver.getStyleDefinitions();

  const allowedStyles = filterStylesByActionName(actionName, styles);

  groups.push(
    ...applyGrouping(actionName, editorPointer, allowedStyles, context),
  );

  const childrenContexts = context
    .getChildren()
    .filter((api) => api.resolver.isElementEditable());

  if (
    childrenContexts &&
    manifestProcessingStrategy === ManifestProcessingStrategy.Recursive
  ) {
    childrenContexts.forEach((childContext) => {
      const childPointer: EditorPointer<CompRef> | null =
        childContext.getEditorPointer();

      if (!childPointer) {
        return;
      }

      processElement(
        actionName,
        childPointer,
        childContext,
        manifestProcessingStrategy,
        groups,
      );
    });
  }
};

const normalizeChangesForEngine = (
  newStyles: Record<string, string>,
  namespaceType: StyleNamespace,
  styleDefinitions: CssCustomProperties,
): Record<string, string> => {
  if (namespaceType === STYLE_NAMESPACE.CssProperties) {
    return newStyles;
  }

  return Object.entries(newStyles).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      const propertyType = styleDefinitions[key]?.cssPropertyType;

      if (propertyType) {
        acc[propertyType] = value;
      }

      return acc;
    },
    {},
  );
};

const createHost = async (
  leadingGroup: LeadingStyleGroup,
  actionName: StylesDerivedNativeActionNames,
  getHost: GlobalDesignPanelProps['getHost'],
  componentsAPI: GlobalDesignPanelProps['componentsAPI'],
) => {
  const styleDefinitions = leadingGroup.context.resolver.getStyleDefinitions();

  const initialStyles = createStylesObject(
    leadingGroup.styleNamespace,
    getStylesInfoFromGroup(leadingGroup.items),
  );

  const allowedCssProps = new Set(
    Object.keys(initialStyles.cssProperties ?? {}),
  );
  const allowedCssCustomProps = new Set(
    Object.keys(initialStyles.cssCustomProperties ?? {}),
  );

  const setters = getSetters(
    leadingGroup.subsetGroups,
    leadingGroup.comparisonMode,
  );

  const themeVariables = new ThemesProvider(componentsAPI).themeVariables;

  const host = await getHost({
    editorPointers: [leadingGroup.editorPointer],
    contextBuilder: (builder) => {
      builder.withFilters((manifestProp, key) => {
        switch (manifestProp) {
          case 'cssProperties':
            return allowedCssProps.has(key);
          case 'cssCustomProperties':
            return allowedCssCustomProps.has(key);
          case 'presets':
          case 'states':
            return true;
          default:
            return false;
        }
      });

      builder.overrideDependencyMethod(
        builder.DependencyToken.STYLES_UPDATE_ROUTER,
        () => {
          return (setStyles) => (editorPointer, type, styles) => {
            setStyles(editorPointer, type, styles);

            const changes = styles[leadingGroup.styleNamespace];

            if (!changes) {
              return;
            }

            if (!componentsAPI.experimentsAPI.enabled(GD_WDS_EXPERIMENT)) {
              const changesMap = processStyleChanges(
                changes,
                leadingGroup.styleNamespace,
                styleDefinitions.cssCustomProperties,
                setters,
                themeVariables,
              );

              const changesMapEntries = Array.from(changesMap.entries());

              const logs = [];

              for (const [
                changeEditorPointer,
                { styleValues, type: targetType },
              ] of changesMapEntries) {
                try {
                  setStyles(changeEditorPointer, targetType, styleValues);
                  logs.push({
                    isFailed: false,
                    editorPointer: changeEditorPointer,
                    styleValues,
                  });
                } catch (_) {
                  logs.push({
                    isFailed: true,
                    editorPointer: changeEditorPointer,
                    styleValues,
                  });
                }
              }

              GlobalDesignPanelLogger.logChanges(logs);
            } else {
              const normalizedChanges = normalizeChangesForEngine(
                changes,
                leadingGroup.styleNamespace,
                styleDefinitions.cssCustomProperties,
              );

              if (Object.keys(normalizedChanges).length === 0) {
                return;
              }

              updateSubsetGroupsStyles({
                subsetGroups: leadingGroup.subsetGroups,
                actionName,
                newStyles: normalizedChanges,
                componentsAPI,
                setStyles,
              })
                .then((logs) => {
                  GlobalDesignPanelLogger.logChanges(logs);
                })
                .catch((error) => {
                  console.error(
                    '[GlobalDesignPanel] Failed to update subset groups',
                    error,
                  );
                });
            }
          };
        },
      );
    },
  });

  return host;
};

export const groupByGroupDefinitions = (
  editorPointers: EditorPointer<CompRef>[],
  actionName: StylesDerivedNativeActionNames,
  getHost: GlobalDesignPanelProps['getHost'],
  componentsAPI: GlobalDesignPanelProps['componentsAPI'],
): GlobalDesignStyleGroup[] => {
  if (editorPointers.length === 0) {
    return [];
  }

  try {
    let groups: StylesGroup[] = [];
    const rootContexts: VirtualManifestAPI[] = [];

    editorPointers.forEach((editorPointer) => {
      const context = componentsAPI.buildManifestAPI({
        editorPointers: [editorPointer],
      });
      rootContexts.push(context);

      const manifestProcessingStrategy =
        GLOBAL_DESIGN_GROUPS_ACTIONS[
          actionName
        ]?.getManifestProcessingStrategy?.(context) ||
        ManifestProcessingStrategy.Recursive;

      processElement(
        actionName,
        editorPointer,
        context,
        manifestProcessingStrategy,
        groups,
      );
    });

    // Create engine for each group
    groups = groups.map((group) => ({
      ...group,
      engine: getStylesMetadata(group, componentsAPI),
    }));

    // Apply group display filter BEFORE building leading groups and detecting subsets
    const groupDisplayFilter =
      GLOBAL_DESIGN_GROUPS_ACTIONS[actionName]?.groupDisplayFilter;

    if (groupDisplayFilter) {
      // TODO: Update groups display filter to use engine
      groups = groupDisplayFilter(groups, rootContexts);
    }

    const comparisonMode =
      GLOBAL_DESIGN_GROUPS_ACTIONS[actionName]?.comparisonMode;
    const valueSetValueFilter =
      GLOBAL_DESIGN_GROUPS_ACTIONS[actionName]?.valueSetValueFilter;

    const leadingGroups = buildLeadingGroups(
      groups,
      comparisonMode,
      actionName,
      valueSetValueFilter,
    );

    GlobalDesignPanelLogger.logGlobalDesignGroups(leadingGroups);

    const styleGroups: GlobalDesignStyleGroup[] = [];

    for (const leadingGroup of leadingGroups) {
      styleGroups.push({
        displayName: leadingGroup.displayName,
        createHost: () =>
          createHost(leadingGroup, actionName, getHost, componentsAPI),
        engine: leadingGroup.engine, // Transfer engine as single source of truth
        subsetGroups: leadingGroup.subsetGroups,
        metadata: {
          targetComponents: getTargetComponents(leadingGroup),
          isBrandColorSupported: getIsBrandColorSupported(leadingGroup),
          editorPointer: leadingGroup.editorPointer,
        },
      });
    }

    return styleGroups;
  } catch (_) {
    return [];
  }
};
